import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import "./AddBusiness.css";
import SuccessPopup from "./SuccessPopup";

const AddBusiness = ({ onBusinessAdded }) => {
  const [name, setName] = useState("");
  const [b_stadd, setBStadd] = useState("");
  const [b_suite, setBSuite] = useState("");
  const [stateCode, setStateCode] = useState("");
  const [b_city, setBCity] = useState("");
  const [b_zip, setBZip] = useState("");
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [counties, setCounties] = useState([]);
  const [chargesCountyTax, setChargesCountyTax] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [b_county, setBCounty] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await api.get("/states");
        setStates(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.error("Error fetching states:", err);
      }
    };

    fetchStates();
  }, []);

  useEffect(() => {
    const fetchCities = async () => {
      if (!stateCode) {
        setCities([]);
        return;
      }

      try {
        const response = await api.get(`/cities/${stateCode}`);
        setCities(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.error("Error fetching cities:", err);
      }
    };

    fetchCities();
  }, [stateCode]);

  const fetchCounties = async (selectedStateCode) => {
    try {
      const response = await api.get(`/counties/${selectedStateCode}`);
      setCounties(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error("Error fetching counties:", err);
    }
  };

  const handleStateChange = (e) => {
    const selectedCode = e.target.value;
    const selectedState = states.find((state) => state.st_code === selectedCode);

    setStateCode(selectedCode);
    setBCity("");
    setBCounty("");

    if (!selectedState) {
      setChargesCountyTax(false);
      setCounties([]);
      return;
    }

    setChargesCountyTax(selectedState.charges_countytax);

    if (selectedState.charges_countytax) {
      fetchCounties(selectedState.st_code);
    } else {
      setCounties([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSubmitting(true);

      await api.post("/businesses", {
        name,
        b_stadd,
        b_suite,
        b_city,
        b_zip,
        b_state: stateCode,
        b_county: chargesCountyTax ? b_county : null,
      });

      setShowSuccessPopup(true);

      if (onBusinessAdded) {
        onBusinessAdded();
      }
    } catch (err) {
      console.error("Error adding business:", err.response?.data || err.message);
      alert("Failed to add business.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="add-bus-page">
      <div className="add-bus-header">
        <h1 className="add-bus-title">Add Business</h1>
        <p className="add-bus-subtitle">
          Enter the business details exactly as they should appear in your system.
        </p>
      </div>

      <div className="add-bus-card">
        <form className="add-bus-form" onSubmit={handleSubmit}>
          <div className="add-bus-field">
            <label htmlFor="addBusName">Business Name</label>
            <input
              id="addBusName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Business Name as it appears on Tax Returns"
              required
            />
          </div>

          <div className="add-bus-field">
            <label htmlFor="addBusStreet">Street Address</label>
            <input
              id="addBusStreet"
              value={b_stadd}
              onChange={(e) => setBStadd(e.target.value)}
              placeholder="Street Address"
              required
            />
          </div>

          <div className="add-bus-grid add-bus-grid-two">
            <div className="add-bus-field">
              <label htmlFor="addBusSuite">Suite</label>
              <input
                id="addBusSuite"
                value={b_suite}
                onChange={(e) => setBSuite(e.target.value)}
                placeholder="Suite (Optional)"
              />
            </div>

            <div className="add-bus-field">
              <label htmlFor="addBusZip">ZIP Code</label>
              <input
                id="addBusZip"
                value={b_zip}
                onChange={(e) => setBZip(e.target.value)}
                placeholder="ZIP Code"
                required
              />
            </div>
          </div>

          <div className="add-bus-grid add-bus-grid-two">
            <div className="add-bus-field">
              <label htmlFor="addBusState">State</label>
              <select
                id="addBusState"
                value={stateCode}
                onChange={handleStateChange}
                required
              >
                <option value="" disabled>
                  Select State
                </option>
                {states.map((state) => (
                  <option key={state.st_code} value={state.st_code}>
                    {state.st_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="add-bus-field">
              <label htmlFor="addBusCity">City</label>
              <select
                id="addBusCity"
                value={b_city}
                onChange={(e) => setBCity(e.target.value)}
                required
                disabled={!stateCode}
              >
                <option value="" disabled>
                  Select City
                </option>
                {cities.map((city, index) => (
                  <option key={index} value={city.city_name}>
                    {city.city_name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {chargesCountyTax === 1 && (
            <div className="add-bus-field">
              <label htmlFor="addBusCounty">County</label>
              <select
                id="addBusCounty"
                value={b_county}
                onChange={(e) => setBCounty(e.target.value)}
                required
              >
                <option value="" disabled>
                  Select County
                </option>
                {counties.map((county, index) => (
                  <option key={index} value={county.county_name}>
                    {county.county_name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="add-bus-actions">
            <button className="add-bus-button" type="submit" disabled={submitting}>
              {submitting ? "Adding..." : "Add Business"}
            </button>
          </div>
        </form>
      </div>

      {showSuccessPopup && (
        <SuccessPopup
          title="Business Added!"
          message="Your business has been successfully added."
          buttonLabel="Go to Dashboard"
          onClose={() => {
            setShowSuccessPopup(false);
            navigate("/dashboard");
          }}
        />
      )}
    </div>
  );
};

export default AddBusiness;


// import React, { useState, useEffect } from "react";
// import { useNavigate } from "react-router-dom";
// import api from "../services/api";
// import "./AddBusiness.css";
// import SuccessPopup from "./SuccessPopup";

// const AddBusiness = ({ onBusinessAdded }) => {
//     const [name, setName] = useState("");
//     const [b_stadd, setBStadd] = useState("");
//     const [b_suite, setBSuite] = useState("");
//     const [stateCode, setStateCode] = useState("");
//     const [b_city, setBCity] = useState("");
//     const [b_zip, setBZip] = useState("");
//     const [states, setStates] = useState([]);
//     const [cities, setCities] = useState([]);
//     const [counties, setCounties] = useState([]); // NEW: State to hold counties
//     const [chargesCountyTax, setChargesCountyTax] = useState(false); // NEW: Tracks if counties are needed
//     const [showSuccessPopup, setShowSuccessPopup] = useState(false);
//     const [b_county, setBCounty] = useState(""); // NEW: Holds selected county
//     const navigate = useNavigate();

//     // Fetch states on component mount
//     useEffect(() => {
//         const fetchStates = async () => {
//             try {
//                 const response = await api.get("/states");
//                 setStates(response.data); // Includes charges_countytax
//             } catch (err) {
//                 console.error("Error fetching states:", err);
//             }
//         };
//         fetchStates();
//     }, []);

//     // Fetch cities when a state is selected (existing logic)
//     useEffect(() => {
//         const fetchCities = async () => {
//             if (stateCode) {
//                 try {
//                     const response = await api.get(`/cities/${stateCode}`);
//                     setCities(response.data);
//                 } catch (err) {
//                     console.error("Error fetching cities:", err);
//                 }
//             }
//         };
//         fetchCities();
//     }, [stateCode]);

//     // Fetch counties if the state charges county tax
//     const fetchCounties = async (stateCode) => {
//         try {
//             const response = await api.get(`/counties/${stateCode}`);
//             setCounties(response.data);
//         } catch (err) {
//             console.error("Error fetching counties:", err);
//         }
//     };

//     // Handle state selection and determine if county tax applies
//     const handleStateChange = (e) => {
//         const selectedState = states.find((state) => state.st_code === e.target.value);
//         setStateCode(selectedState.st_code);
//         setChargesCountyTax(selectedState.charges_countytax); // Check if counties are needed

//         if (selectedState.charges_countytax) {
//             fetchCounties(selectedState.st_code); // Fetch counties if needed
//         } else {
//             setCounties([]); // Clear counties
//             setBCounty(""); // Reset county selection to prevent lingering value
//         }
//     };



//     const handleSubmit = async (e) => {
//         e.preventDefault();

//         try {
//             await api.post("/businesses", {
//                 name,
//                 b_stadd,
//                 b_suite,
//                 b_city,
//                 b_zip,
//                 b_state: stateCode,
//                 b_county: chargesCountyTax ? b_county : null, // Only include county if applicable
//             });
//             setShowSuccessPopup(true);


//             if (onBusinessAdded) {
//                 onBusinessAdded(); // Refresh businesses
//             }

//             navigate("/dashboard");
            
//         } catch (err) {
//             console.error("Error adding business:", err.response?.data || err.message);
//             alert("Failed to add business.");
//         }
//     };

//     return (
//         <div className="add-bus-page">
//             <h2>Add Business</h2>
//             <form className="add-bus-form" onSubmit={handleSubmit}>
//                 <input
//                     value={name}
//                     onChange={(e) => setName(e.target.value)}
//                     placeholder="Business Name as it appears on Tax Returns"
//                     required
//                 />
//                 <input
//                     value={b_stadd}
//                     onChange={(e) => setBStadd(e.target.value)}
//                     placeholder="Street Address"
//                     required
//                 />
//                 <input
//                     value={b_suite}
//                     onChange={(e) => setBSuite(e.target.value)}
//                     placeholder="Suite (Optional)"
//                 />
//                 <select
//                     value={stateCode}
//                     onChange={handleStateChange}
//                     required
//                 >
//                     <option value="" disabled>Select State</option>
//                     {states.map((state) => (
//                         <option key={state.st_code} value={state.st_code}>
//                             {state.st_name}
//                         </option>
//                     ))}
//                 </select>

//                 {/* Render county dropdown only if chargesCountyTax is 1 */}
//                 {chargesCountyTax === 1 && (
//                     <select 
//                         value={b_county}
//                         onChange={(e) => setBCounty(e.target.value)}
//                         required
//                     >
//                         <option value="" disabled>Select County</option>
//                         {counties.map((county, index) => (
//                             <option key={index} value={county.county_name}>
//                                 {county.county_name}
//                             </option>
//                         ))}
//                     </select>
//                 )}


//                 {/* Existing city input */}
//                 <select
//                     value={b_city}
//                     onChange={(e) => setBCity(e.target.value)}
//                     required
//                 >
//                     <option value="" disabled>Select City</option>
//                     {cities.map((city, index) => (
//                         <option key={index} value={city.city_name}>
//                             {city.city_name}
//                         </option>
//                     ))}
//                 </select>

//                 <input
//                     value={b_zip}
//                     onChange={(e) => setBZip(e.target.value)}
//                     placeholder="ZIP Code"
//                     required
//                 />
//                 <button className="add-bus-button" type="submit">Add Business</button>
//             </form>
//             {showSuccessPopup && (
//                 <SuccessPopup
//                     title="Business Added!"
//                     message="Your business has been successfully added."
//                     buttonLabel="Go to Dashboard"
//                     onClose={() => {
//                         setShowSuccessPopup(false);
//                         navigate("/dashboard");
//                     }}
//                 />
//             )}
//         </div>
//     );
// };

// export default AddBusiness;



// import React, { useState, useEffect } from "react";
// import { useNavigate } from "react-router-dom";
// import api from "../services/api";

// const AddBusiness = () => {
//     const [name, setName] = useState("");
//     const [b_stadd, setBStadd] = useState("");
//     const [b_suite, setBSuite] = useState("");
//     const [stateCode, setStateCode] = useState("");
//     const [b_city, setBCity] = useState("");
//     const [b_zip, setBZip] = useState("");
//     const [states, setStates] = useState([]);
//     const [cities, setCities] = useState([]);
//     const navigate = useNavigate();

//     // Fetch states on component mount
//     useEffect(() => {
//         const fetchStates = async () => {
//             try {
//                 const response = await api.get("/states");
//                 setStates(response.data);
//             } catch (err) {
//                 console.error("Error fetching states:", err);
//             }
//         };
//         fetchStates();
//     }, []);

//     // Fetch cities when a state is selected
//     useEffect(() => {
//         const fetchCities = async () => {
//             if (stateCode) {
//                 try {
//                     const response = await api.get(`/cities/${stateCode}`);
//                     setCities(response.data);
//                 } catch (err) {
//                     console.error("Error fetching cities:", err);
//                 }
//             }
//         };
//         fetchCities();
//     }, [stateCode]);

//     const handleSubmit = async (e) => {
//         e.preventDefault();

//         try {
//             // Make the API call to add a business
//             await api.post("/businesses", {
//                 name,
//                 b_stadd,
//                 b_suite,
//                 b_city,
//                 b_zip,
//                 b_state: stateCode,
//             });
//             alert("Business added successfully!");
//             navigate("/dashboard");
//             window.location.reload();
//         } catch (err) {
//             console.error("Error adding business:", err.response?.data || err.message);
//             alert("Failed to add business.");
//         }
//     };

//     return (
//         <div>
//             <h2>Add Business</h2>
//             <form onSubmit={handleSubmit}>
//                 <input
//                     value={name}
//                     onChange={(e) => setName(e.target.value)}
//                     placeholder="Business Name"
//                     required
//                 />
//                 <input
//                     value={b_stadd}
//                     onChange={(e) => setBStadd(e.target.value)}
//                     placeholder="Street Address"
//                     required
//                 />
//                 <input
//                     value={b_suite}
//                     onChange={(e) => setBSuite(e.target.value)}
//                     placeholder="Suite (Optional)"
//                 />
//                 <select
//                     value={stateCode}
//                     onChange={(e) => setStateCode(e.target.value)}
//                     required
//                 >
//                     <option value="" disabled>Select State</option>
//                     {states.map((state) => (
//                         <option key={state.st_code} value={state.st_code}>
//                             {state.st_name}
//                         </option>
//                     ))}
//                 </select>
//                 <select
//                     value={b_city}
//                     onChange={(e) => setBCity(e.target.value)}
//                     required
//                     disabled={!stateCode}
//                 >
//                     <option value="" disabled>Select City</option>
//                     {cities.map((city, index) => (
//                         <option key={index} value={city.city_name}>
//                             {city.city_name}
//                         </option>
//                     ))}
//                 </select>
//                 <input
//                     value={b_zip}
//                     onChange={(e) => setBZip(e.target.value)}
//                     placeholder="ZIP Code"
//                     required
//                 />
//                 <button type="submit">Add Business</button>
//             </form>
//         </div>
//     );
// };

// export default AddBusiness;



// import React, { useState } from "react";
// import { useNavigate } from "react-router-dom";
// import api from "../services/api";

// const AddBusiness = () => {
//     const [name, setName] = useState("");
//     const [b_stadd, setBStadd] = useState("");
//     const [b_suite, setBSuite] = useState("");
//     const [b_state, setBState] = useState("");
//     const [b_city, setBCity] = useState("");
//     const [b_zip, setBZip] = useState("");
//     const [start_date, setStartDate] = useState("");
//     const [end_date, setEndDate] = useState("");
//     const [pay_date, setPayDate] = useState("");
//     const [frequency, setFrequency] = useState("");

//     const navigate = useNavigate();

//     const handleSubmit = async (e) => {
//         e.preventDefault();

//         try {
//             // Make the API call to add a business
//             await api.post("/businesses", {
//                 name,
//                 b_stadd,
//                 b_suite,
//                 b_state,
//                 b_city,
//                 b_zip,
//                 start_date,
//                 end_date,
//                 pay_date,
//                 frequency,
//             });
//             alert("Business added successfully!");
//             navigate("/dashboard");
//             window.location.reload();
//         } catch (err) {
//             console.error("Error adding business:", err.response?.data || err.message);
//             alert("Failed to add business.");
//         }
//     };

//     return (
//         <div>
//             <h2>Add Business</h2>
//             <form onSubmit={handleSubmit}>
//                 <input
//                     value={name}
//                     onChange={(e) => setName(e.target.value)}
//                     placeholder="Business Name"
//                     required
//                 />
//                 <input
//                     value={b_stadd}
//                     onChange={(e) => setBStadd(e.target.value)}
//                     placeholder="Street Address"
//                     required
//                 />
//                 <input
//                     value={b_suite}
//                     onChange={(e) => setBSuite(e.target.value)}
//                     placeholder="Suite (Optional)"
//                 />
//                 <input
//                     value={b_state}
//                     onChange={(e) => setBState(e.target.value)}
//                     placeholder="State (2-letter code)"
//                     required
//                 />
//                 <input
//                     value={b_city}
//                     onChange={(e) => setBCity(e.target.value)}
//                     placeholder="City"
//                     required
//                 />
//                 <input
//                     value={b_zip}
//                     onChange={(e) => setBZip(e.target.value)}
//                     placeholder="ZIP Code"
//                     required
//                 />
//                 <input
//                     value={start_date}
//                     onChange={(e) => setStartDate(e.target.value)}
//                     type="date"
//                     placeholder="Start Date"
//                 />
//                 <input
//                     value={end_date}
//                     onChange={(e) => setEndDate(e.target.value)}
//                     type="date"
//                     placeholder="End Date"
//                 />
//                 <input
//                     value={pay_date}
//                     onChange={(e) => setPayDate(e.target.value)}
//                     type="date"
//                     placeholder="Pay Date"
//                 />
//                 <input
//                     value={frequency}
//                     onChange={(e) => setFrequency(e.target.value)}
//                     placeholder="Frequency (e.g., Weekly)"
//                 />
//                 <button type="submit">Add Business</button>
//             </form>
//         </div>
//     );
// };

// export default AddBusiness;



// import React, { useState } from "react";
// import { useNavigate } from "react-router-dom";
// import api from "../services/api";

// const AddBusiness = () => {
//     const [name, setName] = useState("");
//     const navigate = useNavigate();

    

//     const handleSubmit = async (e) => {
//         e.preventDefault();

//         try {
//             // Make the API call to add a business
//             await api.post("/businesses", { name });
//             alert("Business added successfully!");
//             navigate("/dashboard");
//             window.location.reload();
//         } catch (err) {
//             console.error("Error adding business:", err.response?.data || err.message);
//             alert("Failed to add business.");
//         }
//     };

//     return (
//         <div>
//             <h2>Add Business</h2>
//             <form onSubmit={handleSubmit}>
//                 <input
//                     value={name}
//                     onChange={(e) => setName(e.target.value)}
//                     placeholder="Business Name"
//                     required
//                 />
//                 <button type="submit">Add Business</button>
//             </form>
//         </div>
//     );
// };

// export default AddBusiness;
